/**
 * Coaching Studio (Pro-tier feature)
 *
 * Overlays a webcam PiP + microphone + optional timer on top of an existing
 * clip, composited live into a single WebM via canvas.captureStream +
 * MediaRecorder. When the coach hits Stop → preview → Save, the blob is
 * uploaded straight to S3 via a presigned PUT, then finalized on the server
 * as a new "coached" clip linked to the parent.
 *
 * No server-side ffmpeg needed. Works in Chrome / Edge / Safari 14.1+.
 */
import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { X, Circle, Square, Mic, Video as VideoIcon, Play, Pause, Save, Loader2, Timer, RotateCcw, Film } from "lucide-react";

const PIP_MIN = 100;
const PIP_MAX = 320;
const PIP_DEFAULT = 160;

const fmtTimer = (ms) => {
  const total = Math.max(0, Math.floor(ms));
  const s = Math.floor(total / 1000);
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  const cs = String(Math.floor((total % 1000) / 10)).padStart(2, "0");
  return h === "00" ? `${m}:${sec}.${cs}` : `${h}:${m}:${sec}.${cs}`;
};

export default function CoachingModal({ clip, onClose, onSaved }) {
  const [permError, setPermError] = useState("");
  const [clipError, setClipError] = useState("");
  const [ready, setReady] = useState(false);
  const [clipReady, setClipReady] = useState(false);
  const [clipBlobUrl, setClipBlobUrl] = useState("");
  const [recording, setRecording] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBlob, setPreviewBlob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(`Coaching — ${clip.sub_label || "clip"}`);
  const [shape, setShape] = useState("circle"); // "circle" | "square"
  const [pip, setPip] = useState({ x: 24, y: 24, size: PIP_DEFAULT });
  const [timerVisible, setTimerVisible] = useState(true);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerElapsed, setTimerElapsed] = useState(0);
  const [clipPlaying, setClipPlaying] = useState(false);

  // Refs for the video/webcam/canvas + streams + recorder
  const clipVideoRef = useRef(null);
  const webcamVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const camStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const timerStartRef = useRef(0);
  const timerAccumRef = useRef(0);
  const clipDimsRef = useRef({ w: 1280, h: 720 });

  // 1. Request webcam+mic when the modal mounts
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 } },
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        camStreamRef.current = s;
        attachWebcam();
        setReady(true);
      } catch (e) {
        setPermError(e.message || "Could not access camera / mic");
      }
    })();
    return () => {
      cancelled = true;
      if (camStreamRef.current) camStreamRef.current.getTracks().forEach((t) => t.stop());
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Re-attach the webcam MediaStream whenever the hidden <video> element
  // is unmounted+remounted (e.g. after "Re-record"). React destroys and
  // recreates the DOM node, and without this the PiP goes black because
  // the new element has no srcObject.
  const attachWebcam = () => {
    const v = webcamVideoRef.current;
    const s = camStreamRef.current;
    if (!v || !s) return;
    if (v.srcObject !== s) {
      v.srcObject = s;
      v.play().catch(() => {});
    }
  };

  // Trigger a re-attach whenever the stage remounts.
  useEffect(() => {
    if (!previewUrl && ready) attachWebcam();
  }, [previewUrl, ready]);

  // 1b. Fetch the clip as a Blob so we can play it from a same-origin
  // blob URL. This sidesteps intermittent Chrome CORS issues where a
  // presigned S3 response gets cached without the Access-Control-Allow-Origin
  // header and subsequent <video crossOrigin> loads fail with code 4.
  //
  // Note: clipReady is set AFTER onLoadedMetadata fires on the hidden
  // video element (see below), not immediately after the blob downloads.
  // This ensures the canvas has been sized correctly before the user can
  // start recording — resizing a canvas mid-record kills the captureStream
  // track and MediaRecorder stops.
  useEffect(() => {
    if (!clip?.stream_url) return;
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      try {
        setClipError("");
        const r = await fetch(clip.stream_url, { mode: "cors", credentials: "omit" });
        if (!r.ok) throw new Error(`Clip download failed (${r.status})`);
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setClipBlobUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setClipError(e.message || "Could not download the clip");
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [clip?.stream_url]);

  // 2. Live timer update while running
  useEffect(() => {
    if (!timerRunning) return;
    const id = setInterval(() => {
      setTimerElapsed(timerAccumRef.current + (Date.now() - timerStartRef.current));
    }, 33);
    return () => clearInterval(id);
  }, [timerRunning]);

  // 3. Composite frame loop — always running once ready. Records too when
  // MediaRecorder is active because it reads canvas.captureStream().
  // IMPORTANT: never mutate canvas.width / canvas.height while a
  // captureStream is active. Chrome ends the video track on resize which
  // causes MediaRecorder to stop mid-record. We size the canvas ONCE
  // when the clip's metadata is available, then leave it alone.
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const draw = () => {
      const canvasNow = canvasRef.current;
      if (!canvasNow) { rafRef.current = null; return; }
      const clipV = clipVideoRef.current;
      const camV = webcamVideoRef.current;
      const cw = canvasNow.width;
      const ch = canvasNow.height;
      // Base: clip pixels (or black if not loaded yet)
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, cw, ch);
      if (clipV && clipV.readyState >= 2) {
        ctx.drawImage(clipV, 0, 0, cw, ch);
      }
      // PiP webcam
      if (camV && camV.readyState >= 2) {
        // Scale pip.size (which is in preview coords / 720p container) to
        // canvas coords proportionally
        const scale = cw / 1280;
        const s = pip.size * scale;
        const px = pip.x * scale;
        const py = pip.y * scale;
        ctx.save();
        if (shape === "circle") {
          ctx.beginPath();
          ctx.arc(px + s / 2, py + s / 2, s / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
        } else {
          // Rounded square with a small radius
          const r = s * 0.08;
          roundRect(ctx, px, py, s, s, r);
          ctx.clip();
        }
        // Mirror the webcam so the coach sees themselves naturally
        ctx.translate(px + s, py);
        ctx.scale(-1, 1);
        // Cover-crop the webcam feed to a square
        const vw = camV.videoWidth || 640;
        const vh = camV.videoHeight || 480;
        const side = Math.min(vw, vh);
        const sx = (vw - side) / 2;
        const sy = (vh - side) / 2;
        ctx.drawImage(camV, sx, sy, side, side, 0, 0, s, s);
        ctx.restore();
        // Ring border for polish
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = Math.max(2, 3 * scale);
        if (shape === "circle") {
          ctx.beginPath();
          ctx.arc(px + s / 2, py + s / 2, s / 2, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          const r = s * 0.08;
          roundRect(ctx, px, py, s, s, r);
          ctx.stroke();
        }
      }
      // Timer overlay
      if (timerVisible) {
        const scale = cw / 1280;
        const elapsed = timerRunning
          ? timerAccumRef.current + (Date.now() - timerStartRef.current)
          : timerAccumRef.current;
        const label = fmtTimer(elapsed);
        const fontPx = Math.round(56 * scale);
        // Monospace = every digit has the same width → no left-right jitter as
        // seconds tick over. Right-align the text against a fixed anchor so
        // the box + text never moves.
        ctx.font = `700 ${fontPx}px "SF Mono", "Menlo", "Consolas", "Courier New", monospace`;
        ctx.textBaseline = "top";
        ctx.textAlign = "left";
        // Pre-measure a max-length reference so the box width is constant.
        // Longest possible label is "HH:MM:SS.cc" (11 chars).
        const refLabel = label.length > 8 ? "00:00:00.00" : "00:00.00";
        const refWidth = ctx.measureText(refLabel).width;
        const padX = 24 * scale;
        const padY = 12 * scale;
        const boxW = refWidth + padX * 2;
        const boxH = fontPx + padY * 2;
        const bx = cw - boxW - 24 * scale;
        const by = 24 * scale;
        ctx.fillStyle = "rgba(0,0,0,0.65)";
        ctx.fillRect(bx, by, boxW, boxH);
        ctx.fillStyle = timerRunning ? "#f97316" : "#e7e5e4";
        // Right-align the current label inside the fixed box
        const labelWidth = ctx.measureText(label).width;
        ctx.fillText(label, bx + boxW - padX - labelWidth, by + padY);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [ready, pip, shape, timerVisible, timerRunning, permError, previewUrl]);

  const toggleClipPlayback = () => {
    const v = clipVideoRef.current;
    if (!v) return;
    if (v.ended) {
      // Replay from the top → reset the timer so the second attempt
      // gets a clean run time overlaid.
      try { v.currentTime = 0; } catch {}
      timerAccumRef.current = 0;
      setTimerElapsed(0);
      setTimerRunning(false);
      v.play().catch(() => {});
      return;
    }
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  // Phase label for the coach: intro (clip untouched), run (clip playing),
  // paused talk-over (clip paused mid-way), outro (clip finished).
  const clipEnded = clipVideoRef.current?.ended;
  const phase = !recording ? "" :
    (timerElapsed === 0 && !timerRunning) ? "intro" :
    clipEnded ? "outro" :
    timerRunning ? "run" : "paused";

  const clipToggleLabel = (() => {
    if (!clipVideoRef.current) return "Play clip";
    if (clipEnded) return "Play clip again";
    if (clipPlaying) return "Pause clip · Talk it through";
    return timerElapsed === 0 ? "Play clip · Start the run" : "Resume clip";
  })();

  // 4. Recording controls
  const startRecording = () => {
    const canvas = canvasRef.current;
    const clipV = clipVideoRef.current;
    if (!canvas || !clipV || !camStreamRef.current) return;
    // Rewind to frame 0 so the intro shows the first frame of the clip.
    // Do NOT auto-play the clip — the coach records their intro first
    // (webcam + voice, clip paused on frame 0, timer at 00:00), then hits
    // Play clip when they're ready. Timer auto-starts on play, pauses on
    // pause, and freezes when the clip ends — giving them a natural
    // outro window before they hit Stop.
    try { clipV.currentTime = 0; } catch {}
    timerAccumRef.current = 0;
    setTimerElapsed(0);
    setTimerRunning(false);
    setPreviewUrl(""); setPreviewBlob(null);

    // Compose the stream: canvas video + mic audio only. The clip's own
    // audio is deliberately excluded (and the <video> element is muted)
    // — coaching sessions capture the coach's voice, not the original run.
    const canvasStream = canvas.captureStream(30);
    const mic = camStreamRef.current.getAudioTracks();
    const combined = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...mic,
    ]);
    // Pick a mime type the browser supports (Safari falls back to mp4)
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    const mimeType = candidates.find((c) => window.MediaRecorder && MediaRecorder.isTypeSupported(c)) || "";
    const rec = new MediaRecorder(combined, mimeType ? { mimeType, videoBitsPerSecond: 4_000_000 } : {});
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const type = rec.mimeType || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      setPreviewBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
    };
    rec.start(500);
    recorderRef.current = rec;
    // Do NOT auto-play the clip. Coach records their intro, then hits
    // "Play clip" to start the run analysis.
    setRecording(true);
    setClipPlaying(false);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (clipVideoRef.current) clipVideoRef.current.pause();
    // Snapshot final timer state
    if (timerRunning) {
      timerAccumRef.current = timerAccumRef.current + (Date.now() - timerStartRef.current);
      setTimerRunning(false);
    }
    setRecording(false);
  };

  const toggleTimer = () => {
    if (timerRunning) {
      timerAccumRef.current = timerAccumRef.current + (Date.now() - timerStartRef.current);
      setTimerRunning(false);
    } else {
      timerStartRef.current = Date.now();
      setTimerRunning(true);
    }
  };

  const resetTimer = () => {
    timerAccumRef.current = 0;
    setTimerElapsed(0);
    setTimerRunning(false);
  };

  const discardPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setPreviewBlob(null);
  };

  // 5. Upload the composed blob → S3 → server finalize
  const savePreview = async () => {
    if (!previewBlob) return;
    setSaving(true);
    try {
      const ct = previewBlob.type || "video/webm";
      const init = await api.post(`/box/clips/${clip.id}/coach-init`, { content_type: ct });
      const { clip_id, upload_url } = init.data;
      const put = await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": ct },
        body: previewBlob,
      });
      if (!put.ok) throw new Error(`S3 upload failed: ${put.status}`);
      // Measure duration client-side (works even if the clip video already
      // exposes it; MediaRecorder blobs don't always carry metadata)
      const duration = await probeBlobDuration(previewBlob);
      const fin = await api.post(`/box/clips/${clip.id}/coach-finalize`, {
        clip_id,
        duration,
        size_bytes: previewBlob.size,
        title,
      });
      toast.success("Coaching session saved");
      onSaved && onSaved(fin.data.clip);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // PiP drag/resize
  const onPipMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const start = { ...pip };
    const container = e.currentTarget.parentElement.getBoundingClientRect();
    const move = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      // Preview container is a 1280x720 stage; clamp there
      const stageW = container.width;
      const stageH = container.height;
      const scale = 1280 / stageW; // convert screen px → stage px
      setPip({
        ...start,
        x: Math.max(0, Math.min(1280 - start.size, start.x + dx * scale)),
        y: Math.max(0, Math.min(720 - start.size, start.y + dy * scale)),
      });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  return (
    <div className="fixed inset-0 bg-black/95 z-[70] flex items-center justify-center p-4" data-testid="coaching-modal">
      <div className="bg-stone-950 border border-stone-800 max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-800">
          <div>
            <div className="label-tiny mb-1 text-orange-500">Coaching Studio · Pro</div>
            <div className="font-display uppercase text-xl font-bold">
              {clip.sub_label || "Untitled clip"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 border border-stone-700 hover:border-orange-500 text-stone-300 hover:text-orange-400"
            data-testid="coach-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 grid lg:grid-cols-[1fr_260px] gap-4">
          {/* Stage */}
          <div>
            {permError ? (
              <div className="aspect-video bg-black border border-rose-900 flex items-center justify-center text-center p-8">
                <div>
                  <VideoIcon size={40} className="mx-auto text-rose-500 mb-3" />
                  <div className="font-display uppercase text-lg font-bold mb-1">Camera / mic blocked</div>
                  <div className="text-stone-400 text-sm">{permError}</div>
                  <div className="text-stone-500 text-xs mt-3">Enable camera + microphone permissions and reload.</div>
                </div>
              </div>
            ) : clipError ? (
              <div className="aspect-video bg-black border border-rose-900 flex items-center justify-center text-center p-8">
                <div>
                  <Film size={40} className="mx-auto text-rose-500 mb-3" />
                  <div className="font-display uppercase text-lg font-bold mb-1">Clip failed to load</div>
                  <div className="text-stone-400 text-sm">{clipError}</div>
                  <div className="text-stone-500 text-xs mt-3">Close and try again — S3 links expire after 4 hours.</div>
                </div>
              </div>
            ) : previewUrl ? (
              <div>
                <div className="aspect-video bg-black relative">
                  <video src={previewUrl} controls autoPlay className="w-full h-full object-contain" data-testid="coach-preview-video" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-2">Preview — save or record again</div>
              </div>
            ) : (
              <div className="relative bg-black overflow-hidden" style={{ aspectRatio: "16/9" }} data-testid="coach-stage">
                {/* Hidden source clip video — feeds the canvas. NOT display:none
                    because Safari (and Chrome sometimes) refuse to play a
                    display:none <video>. We keep it in-DOM but off-screen.
                    Also muted — coaching sessions only capture the coach's
                    mic voice, not the original clip's audio.
                    Uses a same-origin blob URL (fetched in the effect above)
                    so canvas.drawImage never taints and captureStream works. */}
                <video
                  ref={clipVideoRef}
                  src={clipBlobUrl || undefined}
                  playsInline
                  preload="auto"
                  muted
                  style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: 0, top: 0 }}
                  onLoadedMetadata={(e) => {
                    const el = e.currentTarget;
                    const w = el.videoWidth || 1280;
                    const h = el.videoHeight || 720;
                    clipDimsRef.current = { w, h };
                    // Size the canvas ONCE, right now, before any
                    // captureStream is ever pulled from it — Chrome ends
                    // the video track if the canvas resizes mid-stream.
                    const c = canvasRef.current;
                    if (c && (c.width !== w || c.height !== h)) {
                      c.width = w;
                      c.height = h;
                    }
                    // Only now is it safe to expose Start Recording — the
                    // canvas is sized to match and won't be touched again.
                    setClipReady(true);
                  }}
                  onPlay={() => {
                    setClipPlaying(true);
                    if (recording && !timerRunning) {
                      timerStartRef.current = Date.now();
                      setTimerRunning(true);
                    }
                  }}
                  onPause={() => {
                    setClipPlaying(false);
                    if (recording && timerRunning) {
                      timerAccumRef.current = timerAccumRef.current + (Date.now() - timerStartRef.current);
                      setTimerRunning(false);
                    }
                  }}
                  onEnded={() => setClipPlaying(false)}
                  onError={(e) => {
                    const code = e.currentTarget.error?.code;
                    setClipError(`Playback failed (code ${code}). The clip may be corrupted or in an unsupported format.`);
                  }}
                />
                {/* Hidden webcam video — same off-screen technique */}
                <video
                  ref={webcamVideoRef}
                  playsInline
                  muted
                  style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: 0, top: 0 }}
                />
                {/* Live composite — clickable during recording to pause/resume the clip */}
                <canvas
                  ref={canvasRef}
                  onClick={recording ? toggleClipPlayback : undefined}
                  className={`w-full h-full object-contain bg-black ${recording ? "cursor-pointer" : ""}`}
                  data-testid="coach-canvas"
                />
                {/* Pause/Play overlay while recording — a big center chevron
                    the coach can tap on mobile without hunting for the sidebar */}
                {recording && !clipPlaying && (
                  <button
                    onClick={toggleClipPlayback}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors"
                    data-testid="coach-canvas-play"
                    title="Resume clip (timer will resume)"
                  >
                    <div className="h-16 w-16 rounded-full bg-orange-600/90 flex items-center justify-center">
                      <Play size={32} className="text-white translate-x-0.5" />
                    </div>
                  </button>
                )}
                {/* Interactive PiP handle overlay (drag while not recording) */}
                {!recording && (
                  <PipHandle pip={pip} setPip={setPip} shape={shape} onMouseDown={onPipMouseDown} />
                )}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {/* Title */}
            <div>
              <div className="label-tiny mb-1">Session title</div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                className="field-input w-full"
                data-testid="coach-title"
              />
            </div>

            {/* Face shape */}
            <div>
              <div className="label-tiny mb-1">Face bubble</div>
              <div className="grid grid-cols-2 gap-1">
                <button
                  onClick={() => setShape("circle")}
                  className={`btn-secondary inline-flex items-center justify-center gap-1.5 text-xs ${shape === "circle" ? "!border-orange-600 !text-orange-400" : ""}`}
                  data-testid="coach-shape-circle"
                >
                  <Circle size={12} /> Circle
                </button>
                <button
                  onClick={() => setShape("square")}
                  className={`btn-secondary inline-flex items-center justify-center gap-1.5 text-xs ${shape === "square" ? "!border-orange-600 !text-orange-400" : ""}`}
                  data-testid="coach-shape-square"
                >
                  <Square size={12} /> Square
                </button>
              </div>
              <div className="mt-2">
                <input
                  type="range"
                  min={PIP_MIN}
                  max={PIP_MAX}
                  step={4}
                  value={pip.size}
                  onChange={(e) => setPip((p) => ({ ...p, size: parseInt(e.target.value, 10) }))}
                  className="w-full accent-orange-600"
                  data-testid="coach-pip-size"
                />
                <div className="flex justify-between text-[9px] text-stone-500 uppercase tracking-widest">
                  <span>Small</span><span>Large</span>
                </div>
              </div>
              <div className="text-[10px] text-stone-500 mt-1">Drag the face bubble on the preview.</div>
            </div>

            {/* Timer */}
            <div>
              <div className="label-tiny mb-1">Timer</div>
              <div className="flex items-center gap-1 mb-2">
                <label className="inline-flex items-center gap-1 text-[11px] text-stone-400">
                  <input
                    type="checkbox"
                    checked={timerVisible}
                    onChange={(e) => setTimerVisible(e.target.checked)}
                    className="accent-orange-600"
                    data-testid="coach-timer-show"
                  />
                  Show on clip
                </label>
              </div>
              <div className="font-mono-tc text-2xl text-orange-400 border border-stone-800 px-3 py-2 text-center mb-2" data-testid="coach-timer-display">
                {fmtTimer(timerElapsed)}
              </div>
              <button
                onClick={resetTimer}
                className="btn-secondary w-full inline-flex items-center justify-center gap-1.5 text-xs"
                data-testid="coach-timer-reset"
              >
                <RotateCcw size={11} /> Reset timer
              </button>
              <div className="text-[10px] text-stone-500 mt-1">
                Auto-starts when you hit Record. Pauses the moment you pause the clip and resumes when you play it again.
              </div>
            </div>

            {/* Record / Save */}
            <div className="border-t border-stone-800 pt-3 space-y-2">
              {previewUrl ? (
                <>
                  <button
                    onClick={savePreview}
                    disabled={saving}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm disabled:opacity-40"
                    data-testid="coach-save"
                  >
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                    {saving ? "Saving…" : "Save session"}
                  </button>
                  <button
                    onClick={discardPreview}
                    disabled={saving}
                    className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-xs"
                    data-testid="coach-discard"
                  >
                    Re-record
                  </button>
                </>
              ) : recording ? (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 text-center mb-1" data-testid="coach-phase">
                    {phase === "intro" && "Recording · Intro"}
                    {phase === "run" && "Recording · Run"}
                    {phase === "paused" && "Recording · Talking"}
                    {phase === "outro" && "Recording · Outro"}
                  </div>
                  <button
                    onClick={toggleClipPlayback}
                    className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm !border-orange-700 !text-orange-300"
                    data-testid="coach-clip-toggle"
                    title="Timer follows: pausing stops the clock, resuming restarts it"
                  >
                    {clipPlaying ? <><Pause size={13} /> {clipToggleLabel}</> : <><Play size={13} /> {clipToggleLabel}</>}
                  </button>
                  <button
                    onClick={stopRecording}
                    className="w-full inline-flex items-center justify-center gap-2 text-sm px-4 py-3 border-2 border-rose-600 bg-rose-600/20 text-rose-300 hover:bg-rose-600/30 font-display uppercase tracking-widest"
                    data-testid="coach-stop"
                  >
                    <span className="inline-block h-2 w-2 bg-rose-500 animate-pulse" />
                    Stop recording
                  </button>
                </>
              ) : (
                <button
                  onClick={startRecording}
                  disabled={!ready || !clipReady || !!permError || !!clipError}
                  className="w-full inline-flex items-center justify-center gap-2 text-sm px-4 py-3 border-2 border-orange-600 bg-orange-600/20 text-orange-300 hover:bg-orange-600/30 font-display uppercase tracking-widest disabled:opacity-40"
                  data-testid="coach-record"
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-orange-500" />
                  Start recording
                </button>
              )}
              {(!ready || !clipReady) && !permError && !clipError && (
                <div className="text-[11px] text-stone-500 text-center flex items-center justify-center gap-1">
                  <Loader2 size={10} className="animate-spin" />
                  {!ready ? "Requesting camera + mic…" : "Loading clip…"}
                </div>
              )}
              {ready && clipReady && !recording && !previewUrl && (
                <div className="text-[10px] text-stone-500 text-center flex items-center justify-center gap-2">
                  <Mic size={10} /> Mic ready · <VideoIcon size={10} /> Cam ready · <Film size={10} /> Clip ready
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PipHandle({ pip, setPip, shape, onMouseDown }) {
  // Show a translucent draggable overlay matching the PiP box (in stage 1280x720 coords).
  return (
    <div
      onMouseDown={onMouseDown}
      className={`absolute cursor-move ${shape === "circle" ? "rounded-full" : "rounded-md"} border-2 border-orange-500/60 bg-orange-500/10`}
      style={{
        left: `${(pip.x / 1280) * 100}%`,
        top: `${(pip.y / 720) * 100}%`,
        width: `${(pip.size / 1280) * 100}%`,
        aspectRatio: "1/1",
      }}
      data-testid="coach-pip-handle"
    />
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

async function probeBlobDuration(blob) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.src = url;
    v.onloadedmetadata = () => {
      // Chrome sometimes reports Infinity for MediaRecorder blobs — seek to fix
      if (v.duration === Infinity) {
        v.currentTime = 1e9;
        v.ontimeupdate = () => {
          v.ontimeupdate = null;
          const d = v.duration;
          URL.revokeObjectURL(url);
          resolve(isFinite(d) ? d : 0);
        };
      } else {
        const d = v.duration;
        URL.revokeObjectURL(url);
        resolve(isFinite(d) ? d : 0);
      }
    };
    v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
  });
}
